/**
 * PaymentProgressTimeline Component
 * Visual timeline showing cumulative payment progress with milestone markers
 */

import React, { useState } from 'react';
import { formatCurrency } from '../../utils/formatters';

const PaymentProgressTimeline = ({
  summary,
  milestones,
  onRecordPayment,
  onEditMilestone,
  onEditPayment,
  onMilestoneClick,
  onIssueNotice,
  onCancelNotice
}) => {
  const [expandedMilestone, setExpandedMilestone] = useState(null);

  const { totalPackagePrice, totalPaidAmount, totalPercentagePaid, nextMilestone } = summary;
  const hasVOs = (summary.voCount || 0) > 0;
  const voTotalAmount = summary.voTotalAmount || 0;
  const grandTotalPrice = summary.grandTotalPrice || totalPackagePrice;
  const grandTotalPaid = summary.grandTotalPaid || totalPaidAmount;
  const grandTotalRemaining = summary.grandTotalRemaining || summary.remainingAmount;

  // Deduction overlay (red segment after the green fill)
  const totalDeductions = summary.totalDeductions || 0;
  const hasDeductions = totalDeductions > 0;
  // Percentage of total (grand) deductions relative to base package so it lines up with the
  // green fill's scale (totalPercentagePaid on base progress bar)
  const deductionPctOfBase = totalPackagePrice > 0
    ? Math.min(100 - Math.min(totalPercentagePaid, 100), (totalDeductions / totalPackagePrice) * 100)
    : 0;
  const greenPct = Math.min(totalPercentagePaid, 100);

  // Milestone type colors
  const typeColors = {
    lock_price: { bg: 'bg-purple-500', ring: 'ring-purple-200', text: 'text-purple-700' },
    site_commencement: { bg: 'bg-blue-500', ring: 'ring-blue-200', text: 'text-blue-700' },
    progress: { bg: 'bg-orange-500', ring: 'ring-orange-200', text: 'text-orange-700' },
    final_completion: { bg: 'bg-green-500', ring: 'ring-green-200', text: 'text-green-700' }
  };

  const statusColors = {
    completed: 'bg-green-500',
    partial: 'bg-yellow-500',
    pending: 'bg-gray-300'
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Summary Stats - stack on mobile */}
      <div className={`grid grid-cols-1 ${hasDeductions ? (hasVOs ? 'sm:grid-cols-5' : 'sm:grid-cols-4') : (hasVOs ? 'sm:grid-cols-4' : 'sm:grid-cols-3')} gap-2 sm:gap-3 text-sm`}>
        <div className="bg-gray-50 rounded-lg p-3 flex sm:flex-col sm:text-center items-center justify-between sm:justify-start">
          <p className="text-gray-500 text-xs sm:mb-1">Base Package</p>
          <p className="font-semibold text-gray-900">{formatCurrency(totalPackagePrice)}</p>
        </div>
        {hasVOs && (
          <div className="bg-purple-50 rounded-lg p-3 flex sm:flex-col sm:text-center items-center justify-between sm:justify-start">
            <p className="text-gray-500 text-xs sm:mb-1">+ Variation Orders</p>
            <p className="font-semibold text-purple-600">{formatCurrency(voTotalAmount)}</p>
          </div>
        )}
        <div className="bg-green-50 rounded-lg p-3 flex sm:flex-col sm:text-center items-center justify-between sm:justify-start">
          <p className="text-gray-500 text-xs sm:mb-1">{hasVOs ? 'Total Collected' : 'Collected'}</p>
          <div className="flex items-center gap-2 sm:flex-col sm:gap-0">
            <p className="font-semibold text-green-600">{formatCurrency(hasVOs ? grandTotalPaid : totalPaidAmount)}</p>
            <p className="text-xs text-green-500">({(hasVOs ? summary.grandTotalPercentagePaid : totalPercentagePaid)?.toFixed(1) || 0}%)</p>
          </div>
        </div>
        {hasDeductions && (
          <div className="bg-red-50 rounded-lg p-3 flex sm:flex-col sm:text-center items-center justify-between sm:justify-start">
            <p className="text-gray-500 text-xs sm:mb-1">Deductions</p>
            <div className="flex items-center gap-2 sm:flex-col sm:gap-0">
              <p className="font-semibold text-red-600">{formatCurrency(totalDeductions)}</p>
              <p className="text-xs text-red-500">
                Refund {formatCurrency(summary.deductionsByType?.refund || 0, false)} · Penalty {formatCurrency(summary.deductionsByType?.late_penalty_interest || 0, false)}
              </p>
            </div>
          </div>
        )}
        <div className="bg-orange-50 rounded-lg p-3 flex sm:flex-col sm:text-center items-center justify-between sm:justify-start">
          <p className="text-gray-500 text-xs sm:mb-1">{hasVOs ? 'Total Remaining' : 'Remaining'}</p>
          <p className="font-semibold text-orange-600">{formatCurrency(hasVOs ? grandTotalRemaining : summary.remainingAmount)}</p>
        </div>
      </div>

      {/* Grand Total display when VOs exist */}
      {hasVOs && (
        <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between">
          <p className="text-sm text-blue-700 font-medium">Grand Total (Base + VO)</p>
          <p className="text-lg font-bold text-blue-700">{formatCurrency(grandTotalPrice)}</p>
        </div>
      )}

      {/* Mobile Progress Bar - Simple version */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Progress</span>
          <span className="font-medium">
            <span className="text-green-600">{totalPercentagePaid.toFixed(1)}%</span>
            {hasDeductions && (
              <span className="text-red-600"> · −{deductionPctOfBase.toFixed(1)}%</span>
            )}
          </span>
        </div>
        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="absolute left-0 top-0 h-2 bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500"
            style={{ width: `${greenPct}%` }}
          />
          {hasDeductions && (
            <div
              className="absolute top-0 h-2 bg-red-500 transition-all duration-500"
              style={{ left: `${greenPct}%`, width: `${deductionPctOfBase}%` }}
            />
          )}
        </div>
      </div>

      {/* Desktop Progress Timeline - Hidden on mobile */}
      <div className="hidden sm:block relative">
        {/* Progress bar background */}
        <div className="h-3 bg-gray-200 rounded-full relative overflow-hidden">
          {/* Filled progress */}
          <div
            className="absolute left-0 top-0 h-3 bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500"
            style={{ width: `${greenPct}%` }}
          />
          {/* Deductions overlay (red) */}
          {hasDeductions && (
            <div
              className="absolute top-0 h-3 bg-red-500 transition-all duration-500"
              style={{ left: `${greenPct}%`, width: `${deductionPctOfBase}%` }}
              title={`Deductions: ${formatCurrency(totalDeductions)}`}
            />
          )}

          {/* Milestone markers */}
          {milestones.map((milestone, index) => {
            const position = milestone.cumulativePercentage;
            const colors = typeColors[milestone.milestoneType] || typeColors.progress;
            const isCompleted = milestone.status === 'completed';
            const isCurrent = milestone.status === 'partial';
            const isNext = nextMilestone?.id === milestone.id;

            return (
              <button
                key={milestone.id}
                onClick={() => {
                  setExpandedMilestone(expandedMilestone === milestone.id ? null : milestone.id);
                  if (onMilestoneClick) onMilestoneClick(milestone);
                }}
                className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full border-2 border-white shadow-md transition-all duration-200 hover:scale-125 focus:outline-none focus:ring-2 ${colors.ring} ${
                  isCompleted ? colors.bg : isCurrent ? 'bg-yellow-400' : 'bg-gray-300'
                }`}
                style={{ left: `calc(${position}% - 10px)` }}
                title={`${milestone.milestoneName} (${milestone.cumulativePercentage}%)`}
              >
                {isCompleted && (
                  <svg className="w-3 h-3 text-white mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>

        {/* Milestone labels */}
        <div className="relative h-16 mt-2">
          {milestones.map((milestone, index) => {
            const position = milestone.cumulativePercentage;
            const colors = typeColors[milestone.milestoneType] || typeColors.progress;
            const isCompleted = milestone.status === 'completed';

            return (
              <div
                key={milestone.id}
                className="absolute text-center"
                style={{
                  left: `${position}%`,
                  transform: 'translateX(-50%)',
                  width: '80px'
                }}
              >
                <p className={`text-xs font-medium ${isCompleted ? 'text-green-600' : 'text-gray-500'}`}>
                  {milestone.cumulativePercentage}%
                </p>
                <p className={`text-xs truncate ${isCompleted ? 'text-gray-700' : 'text-gray-400'}`}>
                  {milestone.milestoneName}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Next Milestone Callout */}
      {nextMilestone && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-blue-600 uppercase">Next Milestone</span>
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${typeColors[nextMilestone.milestoneType]?.text} bg-white`}>
                  {nextMilestone.cumulativePercentage}%
                </span>
              </div>
              <h3 className="font-semibold text-gray-900 truncate">{nextMilestone.milestoneName}</h3>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-2 text-sm">
                <div>
                  <span className="text-gray-500">Payment Due:</span>
                  <span className="ml-1 font-semibold text-blue-600">{formatCurrency(nextMilestone.paymentDue)}</span>
                </div>
                <div className="hidden sm:block text-gray-400">|</div>
                <div>
                  <span className="text-gray-500">This stage:</span>
                  <span className="ml-1 text-gray-700">{formatCurrency(nextMilestone.individualPayment)} ({nextMilestone.individualPercentage}%)</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => onRecordPayment(nextMilestone)}
              className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:scale-[0.98] font-medium text-sm whitespace-nowrap min-h-[44px] touch-manipulation transition-all"
            >
              Record Payment
            </button>
          </div>
        </div>
      )}

      {/* All Milestones Completed */}
      {!nextMilestone && milestones.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 flex items-center gap-3">
          <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="min-w-0">
            <h3 className="font-semibold text-green-800">All Payments Complete</h3>
            <p className="text-sm text-green-600 truncate">100% of package price collected ({formatCurrency(totalPaidAmount)})</p>
          </div>
        </div>
      )}

      {/* Milestone Details List */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-gray-700">Payment Schedule</h4>
        {milestones.map((milestone, index) => {
          const colors = typeColors[milestone.milestoneType] || typeColors.progress;
          const isExpanded = expandedMilestone === milestone.id;
          const isCompleted = milestone.status === 'completed';
          const isCurrent = milestone.status === 'partial' || nextMilestone?.id === milestone.id;

          return (
            <div
              key={milestone.id}
              className={`border rounded-lg transition-all ${
                isCompleted ? 'border-green-200 bg-green-50/50' :
                isCurrent ? 'border-blue-200 bg-blue-50/50' :
                'border-gray-200 bg-gray-50/50'
              }`}
            >
              <button
                onClick={() => setExpandedMilestone(isExpanded ? null : milestone.id)}
                className="w-full p-3 sm:p-4 flex items-center justify-between text-left min-h-[56px] touch-manipulation"
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-7 h-7 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted ? 'bg-green-500' : isCurrent ? 'bg-blue-500' : 'bg-gray-300'
                  }`}>
                    {isCompleted ? (
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className="text-xs text-white font-medium">{index + 1}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className={`font-medium truncate ${isCompleted ? 'text-green-700' : 'text-gray-900'}`}>
                        {milestone.milestoneName}
                      </p>
                      {milestone.activeNotice && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 font-medium rounded-full">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5" />
                          </svg>
                          Notice · {formatCurrency(milestone.activeNotice.amount, false)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      {milestone.cumulativePercentage}% cumulative target
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2">
                  <div className="text-right">
                    {isCompleted ? (
                      <p className="text-sm font-medium text-green-600">Paid</p>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-gray-900">{formatCurrency(milestone.paymentDue)}</p>
                        <p className="text-xs text-gray-500">due</p>
                      </>
                    )}
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </button>

              {isExpanded && (
                <div className="px-3 sm:px-4 pb-3 sm:pb-4 border-t border-gray-100">
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-3 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs">Stage Payment</p>
                      <p className="font-medium">{formatCurrency(milestone.individualPayment)} ({milestone.individualPercentage}%)</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Cumulative Target</p>
                      <p className="font-medium">{formatCurrency(milestone.cumulativeTarget)}</p>
                    </div>
                  </div>

                  {/* Payment History */}
                  {milestone.paymentRecords && milestone.paymentRecords.length > 0 && (
                    <div className="mt-4 border-t border-gray-100 pt-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">Payment History</p>
                      <div className="space-y-2">
                        {milestone.paymentRecords.map((payment) => (
                          <div key={payment.id} className="bg-white border border-gray-200 rounded-lg p-2 sm:p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-green-600">{formatCurrency(payment.amount)}</span>
                                  <span className="text-xs text-gray-400">•</span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(payment.paymentDate).toLocaleDateString()}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-gray-500">
                                  {payment.paymentMethod && (
                                    <span className="capitalize">{payment.paymentMethod.replace('_', ' ')}</span>
                                  )}
                                  {payment.referenceNumber && (
                                    <>
                                      <span>•</span>
                                      <span>Ref: {payment.referenceNumber}</span>
                                    </>
                                  )}
                                  {payment.recorder && (
                                    <>
                                      <span>•</span>
                                      <span>by {payment.recorder.name}</span>
                                    </>
                                  )}
                                </div>
                                {payment.notes && (
                                  <p className="text-xs text-gray-500 mt-1 italic">{payment.notes}</p>
                                )}
                              </div>
                              {/* Edit Payment Button */}
                              {onEditPayment && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditPayment(payment, milestone);
                                  }}
                                  className="flex-shrink-0 p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors min-h-[36px] min-w-[36px] flex items-center justify-center touch-manipulation"
                                  title="Edit payment"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              )}
                            </div>

                            {/* Receipt Files */}
                            {payment.receiptFiles && payment.receiptFiles.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <p className="text-xs text-gray-500 mb-1">Receipts:</p>
                                <div className="flex flex-wrap gap-2">
                                  {payment.receiptFiles.map((file, fileIndex) => (
                                    <a
                                      key={fileIndex}
                                      href={file.filePath || `/api/project-files/${file.fileId}/download`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded hover:bg-blue-100 transition-colors"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      <span className="truncate max-w-[100px]">{file.fileName || 'Receipt'}</span>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex flex-col sm:flex-row gap-2">
                    {!isCompleted && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRecordPayment(milestone);
                        }}
                        className="flex-1 px-4 py-3 sm:py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 active:scale-[0.98] font-medium min-h-[44px] touch-manipulation transition-all"
                      >
                        Record Payment
                      </button>
                    )}
                    {!isCompleted && onIssueNotice && !milestone.activeNotice && (milestone.paymentDue > 0) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onIssueNotice(milestone);
                        }}
                        className="px-4 py-3 sm:py-2 bg-purple-50 text-purple-700 border border-purple-200 text-sm rounded-lg hover:bg-purple-100 active:scale-[0.98] font-medium min-h-[44px] touch-manipulation transition-all"
                      >
                        Issue Notice
                      </button>
                    )}
                    {milestone.activeNotice && onCancelNotice && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCancelNotice(milestone, milestone.activeNotice.id);
                        }}
                        className="px-4 py-3 sm:py-2 bg-gray-100 text-gray-700 border border-gray-200 text-sm rounded-lg hover:bg-gray-200 active:scale-[0.98] font-medium min-h-[44px] touch-manipulation transition-all"
                      >
                        Cancel Notice
                      </button>
                    )}
                    {onEditMilestone && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditMilestone(milestone);
                        }}
                        className="px-4 py-3 sm:py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 active:scale-[0.98] font-medium min-h-[44px] touch-manipulation transition-all"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentProgressTimeline;
